// @ts-nocheck
import { useThree, useFrame } from '@react-three/fiber';
import { InstantTracker } from '@zappar/zappar-react-three-fiber/lib/index-standalone';
import React, { ReactElement, FunctionComponent, memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { DoubleSide, Frustum, Object3D, Vector3 } from 'three';
import PointerGesture, { GestureMode } from '../../../utils/pointerGesture';
import { ISceneActions, ITransCatTypes, useDataStore, useUserStore, useSceneStore, IEntityActionTypes, useActionStateStore } from '../../../zustand';
import { IActionCategory, IPlaySoundAction, IRoot, ISceneComp, ITuple3 } from '../../r3f-components/component-data-structure';
import { ITrackingTypes } from '../../r3f-components/component-data-structure';
import { ITransitionPhase } from '../../r3f-components/transitions/spec';
import PlacementScene from '../PlacementScene';
import PlacementUI from '../PlacementUI/PlacementUI';
import ShadowPlane from '../../r3f-components/components/ShadowPlane';
import { isAbstractComponent } from '../../r3f-components/utils/general';
import { useGetActiveTrackingType } from '../../hoc/hocCustomHooks';
import { useGetCurrentSceneId } from '../../../hooks/general';
import { presetAnimationController } from '../../PresetAnimationController';
import { getAllAnimationControllerOptions } from '../../../utils/action';
import { getAllOnloadModelAnimationPlayDataBySceneId } from '../../r3f-components/utils/actions';
import { usePlacementBehaviour } from '../trackerGroupHooks';
import PlacementScenes from '../PlacementScenes';

type IParentProps = {
	onShowContentFirstTime?: () => any;
	enabled: boolean;
	children: ReactElement[] | undefined;
};

const WorldInstantTrackerWrapper: FunctionComponent<IParentProps> = ({ onShowContentFirstTime, enabled, children }) => {
	// Zustand
	const sceneDispatch = useSceneStore(({ dispatch }) => dispatch);
	const entityActionDispatch = useActionStateStore(({ dispatch }) => dispatch);
	const componentsById = useDataStore(({ componentsById }) => componentsById);
	const previousSceneTransitionPhase = useSceneStore(({ transitionPhase, previousSceneId }) => transitionPhase[previousSceneId]);
	const previousSceneId = useSceneStore(({ previousSceneId }) => previousSceneId);
	const placementMode = useSceneStore(({ placementMode }) => placementMode);
	const activeTrackingType = useGetActiveTrackingType();
	const currentSceneIsNoTrackingScreen = activeTrackingType === ITrackingTypes.noTrackingScreen;
	const trackingTypeHasChanged = useSceneStore(({ trackingTypeHasChanged }) => trackingTypeHasChanged);
	const isSnapshotUiVisible = useUserStore((state) => state.isSnapshotUiVisible);
	const currentSceneId = useGetCurrentSceneId();
	const firstSceneIsWorldTracked = useDataStore((state) => {
		const firstSceneId = (state.componentsById?.[state.rootId as string] as IRoot)?.children[0];
		return (state.componentsById?.[firstSceneId] as ISceneComp)?.trackingType === 'world';
	});
	const initialPlacementHasOccuredRef = useRef(false);
	const zPlaneDepth = -5;
	const currentSceneIsWorldTracked = (componentsById?.[currentSceneId] as ISceneComp)?.trackingType === ITrackingTypes.world;
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	const previousSceneTrackingTypeRef = useRef<ITrackingTypes>();
	previousSceneTrackingTypeRef.current = (componentsById?.[previousSceneId] as ISceneComp)?.trackingType;

	// Ref
	const initialPlacementCameraOffset: ITuple3 = [0, -0.75, zPlaneDepth];
	const placementCameraOffsetRef = useRef<ITuple3>(initialPlacementCameraOffset);

	// State
	const [groupScale, setGroupScale] = useState<ITuple3>([1, 1, 1]); // Scale applied to placement scene
	const [activeGesture, setActiveGesture] = useState(false); // Whether or not there is an active gesture ( drag / pinch )
	const [pointerGestureHandler, setPointerGestureHandler] = useState<undefined | PointerGesture>(undefined);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [_, setRefresh] = useState(0); // To force a refresh when dragging
    const {enterPlacementBehaviour, exitPlacementBehaviour} = usePlacementBehaviour()

	// Hotspot setup
	const { camera, scene } = useThree();
	const geometry = useMemo(() => <planeGeometry args={[50, 50]} />, []);
	const hotspotPlaneRef = useRef<Object3D>();

	const sceneComp = useMemo(() => componentsById?.[currentSceneId], [currentSceneId, componentsById]) as ISceneComp;
    const sceneOnLoadPlaySoundActions = useMemo(() => sceneComp?.actions?.onLoad?.filter(action => action.type === IActionCategory.playSound), [sceneComp]) as IPlaySoundAction[];

	// Make the plane camera relative
	useFrame(() => {
		if (hotspotPlaneRef.current && hotspotPlaneRef.current.parent !== camera) {
			camera.add(hotspotPlaneRef.current);
		}
	});

	/** GESTURE HANDLERS */
	const onGesturePinchHandler = useCallback((currentDiff: number) => {
		setGroupScale([currentDiff, currentDiff, currentDiff]);
	}, []);

	const onGestureDragHandler = useCallback(
		(delta: number[]) => {
			const frustum = new Frustum().setFromProjectionMatrix(camera.projectionMatrix);
			let prospectivePoint = new Vector3((placementCameraOffsetRef.current[0] || 0) + delta[0], (placementCameraOffsetRef.current[1] || 0) + delta[1], zPlaneDepth);
			// Don't allow dragging the anchor outside of camera view
			if (!frustum.containsPoint(prospectivePoint)) return;
			placementCameraOffsetRef.current = [prospectivePoint.x, prospectivePoint.y, prospectivePoint.z];
			setRefresh((prevState) => prevState + 1);
		},
		[zPlaneDepth, camera.projectionMatrix]
	);

	const onGestureMoveHandler = useCallback((mode: GestureMode) => {
		if (mode !== null) {
			setActiveGesture(true);
		} else {
			setActiveGesture(false);
		}
	}, []);

	const onGestureUpHandler = useCallback(() => {
		setActiveGesture(false);
	}, []);

	// Effects
	useLayoutEffect(() => {
		const pgh = new PointerGesture(camera);
		setPointerGestureHandler(pgh);
	}, [camera]);

	useLayoutEffect(() => {
		if (pointerGestureHandler) {
			pointerGestureHandler.onDragHandler = onGestureDragHandler;
			pointerGestureHandler.onPinchHandler = onGesturePinchHandler;
			pointerGestureHandler.onPointerMoveHandler = onGestureMoveHandler;
			pointerGestureHandler.onPointerUpHandler = onGestureUpHandler;
		}
	}, [onGestureDragHandler, onGesturePinchHandler, onGestureMoveHandler, onGestureUpHandler, pointerGestureHandler]);

	// Required so that the pointer gestures ( drag, pinch ) don't persist after the scene is placed
	useEffect(() => {
		if (!placementMode && pointerGestureHandler?.isActive) {
			pointerGestureHandler?._cleanup();
		}
	}, [placementMode, pointerGestureHandler]);

	// global placement state is false initially to allow pointer events if active scene is image tracked.
	useEffect(() => {
		// if current scene is not world trackend or world tracking isn't enabeld hide placement ui
		if (!enabled || !currentSceneIsWorldTracked) {
			sceneDispatch?.({
				type: ISceneActions.SET_PLACEMENT_MODE,
				placementMode: false,
			});
			return;
		}

		// Prevents emtpy placement ui being shown for menu scenes
		if (currentSceneIsNoTrackingScreen) return;

		// show placement ui if
		// 1) its the first load of the first scene (tracking type hasn't changed & no previous transition phase)
		// 2) tracking type has changed and the previous exit scene has finished.
		if ((!trackingTypeHasChanged && !previousSceneTransitionPhase) || (trackingTypeHasChanged && previousSceneTransitionPhase === ITransitionPhase.exitFinish)) {
			// if switch from face tracking to world add delay to await camera switch
			if (trackingTypeHasChanged && previousSceneTrackingTypeRef.current === ITrackingTypes.face) {
				if (timeoutRef.curret) clearTimeout(timeoutRef.current);
				timeoutRef.current = setTimeout(() => {
					sceneDispatch?.({
						type: ISceneActions.SET_PLACEMENT_MODE,
						placementMode: true,
					});
				}, 500);
				return;
			}
			sceneDispatch?.({
				type: ISceneActions.SET_PLACEMENT_MODE,
				placementMode: true,
			});
		}
	}, [sceneDispatch, currentSceneIsNoTrackingScreen, enabled, previousSceneTransitionPhase, currentSceneIsWorldTracked, trackingTypeHasChanged]);

	return (
		// show placement ui and placement scene if tracking is enabled, show prompt is true
		// (i.e. tracking has changed/or is on initial first scene) and in placement mode
		<>
			{!isSnapshotUiVisible && (
				<PlacementUI
					activeGesture={activeGesture}
					enabled={enabled}
					onPlacementModeChange={() => {
						if (!placementMode) {
							enterPlacementBehaviour();
							// Reset scale of group
							setGroupScale([1, 1, 1]);
							// Reset camera position offset
							placementCameraOffsetRef.current = initialPlacementCameraOffset;
							return;
						} 
						// if first scene is world tracked, world tracked content has never been placed before
						// and placement mode is changed from true to false (at this point still true)
						// the experience content (not placement ui) is shown for first time
						if (!initialPlacementHasOccuredRef.current && placementMode && firstSceneIsWorldTracked) {
							onShowContentFirstTime?.();
						}
						initialPlacementHasOccuredRef.current = true;
						
						exitPlacementBehaviour();
					}}
				/>
			)}
			{/* HOTSPOT FOR DRAGGING */}
			{placementMode && (
				<mesh
					position={[0, 0, zPlaneDepth]}
					visible={false}
					onPointerDown={(e) => pointerGestureHandler?._pointerDownHandler(e)}
					onPointerMove={(e) => pointerGestureHandler?._pointerMoveHandler(e)}
					onPointerUp={(e) => pointerGestureHandler?._pointerUpHandler(e)}
					onPointerCancel={() => pointerGestureHandler?._cleanup()}
					ref={hotspotPlaneRef}
				>
					{geometry}
					<meshBasicMaterial opacity={0} transparent={true} />
				</mesh>
			)}
			{/* END HOTSPOT FOR DRAGGING */}
			{
				<InstantTracker enabled={enabled} name="world_tracker_group" placementMode={placementMode} placementCameraOffset={placementCameraOffsetRef.current}>
					<group scale={groupScale}>
						<group visible={enabled && placementMode}>
							<PlacementScenes />
						</group>
						<group visible={!enabled || !placementMode}>{children}</group>
						{!placementMode && enabled && <ShadowPlane rotation={[-Math.PI / 2, 0, 0]} occlusion opacity={0.3} />}
					</group>
					{placementMode && (
						<mesh position={[0, 0, 0]} rotation={[Math.PI / 2, 0, 0]}>
							<ringGeometry args={[0.25, 0.28, 50]} />
							<meshBasicMaterial color={0xffffff} side={DoubleSide} />
						</mesh>
					)}
				</InstantTracker>
			}
		</>
	);
};

WorldInstantTrackerWrapper.displayName = 'WorldInstantTrackerWrapper';
export default memo(WorldInstantTrackerWrapper);
